import React from "react";

import { ETargetSwitch } from "../organisms/TargetGroup";

type TDefaultTargetSwitch = "BOX" | ETargetSwitch;

interface UseBoxToggleOptions {
  defaultTargetSwitch?: TDefaultTargetSwitch;
  originalGearboxId?: number | null;
}

interface BoxToggleRequiredState {
  isBoxToggleOn: boolean;
  showBoxDropdown: boolean;
  selected_box_id: number | null;
  showSubmitForm: boolean;
  showShareForm: boolean;
  selected_teacher_ids: number[];
  selected_class_ids: number[];
  targetSwitch: ETargetSwitch;
}

export interface UseBoxToggleResult {
  boxDropdownRef: React.RefObject<HTMLDivElement>;
  toggleButtonRef: React.RefObject<HTMLButtonElement>;
  selectedNameRef: React.RefObject<HTMLButtonElement>;
  handleBoxToggle: () => void;
  handleBoxSelect: (args: { [key: string]: any }) => void;
  handleToggleDropdown: () => void;
}

export const useBoxToggle = <State extends BoxToggleRequiredState>(
  setState: React.Dispatch<React.SetStateAction<State>>,
  showBoxDropdown: boolean,
  options: UseBoxToggleOptions = {},
): UseBoxToggleResult => {
  const { defaultTargetSwitch, originalGearboxId = null } = options;

  const boxDropdownRef = React.useRef<HTMLDivElement>(null);
  const toggleButtonRef = React.useRef<HTMLButtonElement>(null);
  const selectedNameRef = React.useRef<HTMLButtonElement>(null);

  const handleBoxToggle = React.useCallback(() => {
    setState(prevState => {
      const nextOn = !prevState.isBoxToggleOn;
      return {
        ...prevState,
        isBoxToggleOn: nextOn,
        showBoxDropdown: nextOn,
        selected_box_id: (() => {
          if (!nextOn) return null;
          if (defaultTargetSwitch === undefined)
            return prevState.selected_box_id;
          return defaultTargetSwitch === "BOX" ? originalGearboxId : null;
        })(),
        showSubmitForm: false,
        showShareForm: false,
      };
    });
  }, [defaultTargetSwitch, originalGearboxId, setState]);

  const handleBoxSelect = React.useCallback(
    (args: { [key: string]: any }) => {
      setState(prevState => ({
        ...prevState,
        ...args,
        showBoxDropdown: false,
        isBoxToggleOn: true,
        selected_teacher_ids: [],
        selected_class_ids: [],
        targetSwitch: ETargetSwitch.ONLY_ME,
        showSubmitForm: false,
        showShareForm: false,
      }));
    },
    [setState],
  );

  const handleToggleDropdown = React.useCallback(() => {
    setState(prevState => ({
      ...prevState,
      showBoxDropdown: !prevState.showBoxDropdown,
    }));
  }, [setState]);

  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        showBoxDropdown &&
        boxDropdownRef.current &&
        !boxDropdownRef.current.contains(e.target as Node) &&
        !toggleButtonRef.current?.contains(e.target as Node) &&
        !selectedNameRef.current?.contains(e.target as Node)
      ) {
        setState(prevState => ({
          ...prevState,
          showBoxDropdown: false,
          isBoxToggleOn: prevState.selected_box_id !== null,
        }));
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showBoxDropdown, setState]);

  return {
    boxDropdownRef,
    toggleButtonRef,
    selectedNameRef,
    handleBoxToggle,
    handleBoxSelect,
    handleToggleDropdown,
  };
};
